import React from 'react'
import { numericSection } from '../utils/sectioned-input/numeric-section'
import { BuildUseSectionedInput } from '../utils/sectioned-input/use-sectioned-input'
import { composeEventHandlers } from '../utils/compose-event-handlers'
import { YearMonthFns } from '../utils/datetime'

export interface HeadlessDateInputProps extends Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'value' | 'defaultValue' | 'onChange'
> {
  value?: string | null
  defaultValue?: string | null
  onChange?(value: string | null): unknown
}

export const HeadlessDateInput = React.forwardRef<HTMLInputElement, HeadlessDateInputProps>(
  ({ value, defaultValue, onChange, ...props }, forwardedRef) => {
    const { inputProps } = useDateInput({
      value,
      defaultValue,
      onChange
    })
    return (
      <input
        {...props}
        {...inputProps}
        onKeyDown={composeEventHandlers(inputProps.onKeyDown, props.onKeyDown)}
        onPaste={composeEventHandlers(inputProps.onPaste, props.onPaste)}
        onMouseUp={composeEventHandlers(inputProps.onMouseUp, props.onMouseUp)}
        onSelect={composeEventHandlers(inputProps.onSelect, props.onSelect)}
        onInput={composeEventHandlers(inputProps.onInput, props.onInput)}
        ref={forwardedRef}
      />
    )
  }
)
HeadlessDateInput.displayName = 'HeadlessDateInput'

const useDateInput = BuildUseSectionedInput({
  sections: [
    numericSection({ min: 1, max: 12, placeholder: 'MM', suffix: ' / ', pad: true }),
    numericSection({
      min: 1,
      max: 31,
      placeholder: 'DD',
      suffix: ' / ',
      pad: true,
      tick(state, { sectionValues, action }) {
        const [monthStr, dayStr, yearStr] = sectionValues
        const year = Number(yearStr)
        const month = Number(monthStr)
        const day = Number(dayStr)
        const daysInMonth = YearMonthFns.getDaysInMonth(`${`${year}`.padStart(4, '0')}-${`${month}`.padStart(2, '0')}`)
        if (Number.isNaN(daysInMonth) || day <= daysInMonth) {
          return sectionValues[1]
        } else {
          if (action === 'increment') {
            return `01`
          } else {
            return `${daysInMonth}`.padStart(2, '0')
          }
        }
      }
    }),
    numericSection({
      min: 0,
      max: 9999,
      placeholder: 'YYYY',
      suffix: '',
      pad: true,
      tick(state, { action }) {
        if (state.value === '0000' && action === 'increment') {
          return `${new Date().getFullYear()}`
        } else if (state.value === '9999' && action === 'decrement') {
          return `${new Date().getFullYear()}`
        } else {
          return state.value
        }
      }
    })
  ],
  externalValueToSectionValues(value) {
    if (!value) {
      return ['MM', 'DD', 'YYYY']
    }
    const [yearStr, monthStr, dayStr] = value.split('-')
    return [monthStr, dayStr, yearStr]
  },
  sectionValuesToExternalValue(sectionValues) {
    const [monthStr, dayStr, yearStr] = sectionValues
    const year = Number(yearStr)
    const month = Number(monthStr)
    const daysInMonth = YearMonthFns.getDaysInMonth(`${year}-${`${month}`.padStart(2, '0')}`)
    const day = Math.min(Number(dayStr), Number.isNaN(daysInMonth) ? 50 : daysInMonth)
    const date = new Date(Date.UTC(year, month - 1, day))
    const invalid = Number.isNaN(date.getTime()) || year < 1000 || year > 9999
    if (invalid) {
      return null
    } else {
      return date.toISOString().slice(0, 10)
    }
  },
  tryParsePaste(value) {
    try {
      const date = new Date(value)
      if (!Number.isNaN(date.getTime())) {
        return [
          `${date.getMonth() + 1}`.padStart(2, '0'),
          `${date.getDate()}`.padStart(2, '0'),
          `${date.getFullYear()}`.padStart(4, '0')
        ]
      } else {
        return null
      }
    } catch {
      return null
    }
  }
})
